const CACHE_PREFIX = "muhasabaty:offline:cache:";
const QUEUE_KEY = "muhasabaty:offline:queue";

const readJson = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
};
const writeJson = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};

export const isOfflineMode = () => typeof navigator !== "undefined" && navigator.onLine === false;
export const cacheKey = (config) => `${(config.method || "get").toLowerCase()}:${config.url}:${JSON.stringify(config.params || {})}`;
export const getCached = (config) => readJson(CACHE_PREFIX + cacheKey(config), null);
export const setCached = (config, data) => writeJson(CACHE_PREFIX + cacheKey(config), { data, cached_at: new Date().toISOString() });

export const getOfflineQueue = () => readJson(QUEUE_KEY, []);

// Flatten any axios/FastAPI error into a short human string. NEVER store the raw
// 422 detail (an array of objects) — rendering it directly crashes the queue page.
const errMsg = (error) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || (typeof x === "string" ? x : JSON.stringify(x))).join(" • ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return error?.message || "فشل التزامن";
};

export const queueRequest = (config) => {
  const queue = getOfflineQueue();
  let payload = config.data;
  // config.data may already be a serialized JSON string (response-interceptor path
  // when a live request fails at the network level). Store a PARSED object so the
  // replay serializes it once — otherwise it gets double-encoded and the backend
  // rejects it with a 422 "Input should be a valid dictionary".
  try { if (typeof payload === "string") payload = JSON.parse(payload); } catch {}
  const idem = payload && (payload.idempotency_key || payload.client_id);
  if (idem && queue.some(x => x.idempotency_key === idem)) return;
  queue.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    method: (config.method || "post").toUpperCase(),
    url: config.url,
    data: payload,
    params: config.params,
    idempotency_key: idem || null,
    attempts: 0,
    created_at: new Date().toISOString(),
  });
  writeJson(QUEUE_KEY, queue);
  window.dispatchEvent(new CustomEvent("offline-queue-changed"));
};

export const removeQueued = (id) => writeJson(QUEUE_KEY, getOfflineQueue().filter(x => x.id !== id));
export const clearOfflineQueue = () => { writeJson(QUEUE_KEY, []); window.dispatchEvent(new CustomEvent("offline-queue-changed")); };

export async function flushOfflineQueue(client) {
  if (isOfflineMode()) return { flushed: 0, failed: 0, remaining: getOfflineQueue().length };
  let flushed = 0, failed = 0;
  for (const item of [...getOfflineQueue()]) {
    if (isOfflineMode()) break; // connection dropped mid-sync → keep the rest for later
    // Legacy items (queued before the object-normalization fix) may hold a JSON string.
    // Parse it so axios serializes exactly once and the backend gets a real object.
    let data = item.data;
    if (typeof data === "string") { try { data = JSON.parse(data); } catch {} }
    try {
      await client({ method: item.method, url: item.url, data, params: item.params, _offlineReplay: true });
      removeQueued(item.id); flushed += 1;
    } catch (error) {
      const isNetwork = !error?.response; // no HTTP response → connectivity issue
      const updated = getOfflineQueue().map(x => x.id === item.id ? {
        ...x,
        attempts: (x.attempts || 0) + 1,
        last_error: errMsg(error),
        last_attempt_at: new Date().toISOString(),
      } : x);
      writeJson(QUEUE_KEY, updated);
      failed += 1;
      // Only stop on a real connectivity loss. A server rejection of ONE item must NOT
      // block the rest of the queue — skip it (kept for retry) and continue syncing.
      if (isNetwork) break;
    }
  }
  window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  return { flushed, failed, remaining: getOfflineQueue().length };
}
